/**
 * React bindings for the overlay ad coordinator singleton.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { OverlayClaim, OverlayLane } from '../../shared/adsense/overlayPriority';
import {
  acquireOverlay,
  configureOverlayCoordinatorFromViteEnv,
  getActiveOverlayClaim,
  releaseOverlay,
  subscribeOverlayClaims,
} from './overlayAdCoordinator';

configureOverlayCoordinatorFromViteEnv(import.meta.env.VITE_APT_OVERLAY_COORDINATOR_ENABLED);

type OverlayAdCoordinatorApi = {
  active: OverlayClaim | null;
  acquire: (lane: OverlayLane, id: string) => boolean;
  release: (lane: OverlayLane, id: string) => void;
};

const OverlayAdCoordinatorContext = createContext<OverlayAdCoordinatorApi | null>(null);

export const OverlayAdCoordinatorProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [active, setActive] = useState<OverlayClaim | null>(() => getActiveOverlayClaim());

  useEffect(() => subscribeOverlayClaims(setActive), []);

  const acquire = useCallback((lane: OverlayLane, id: string) => acquireOverlay(lane, id), []);
  const release = useCallback((lane: OverlayLane, id: string) => releaseOverlay(lane, id), []);

  const value = useMemo(
    () => ({
      active,
      acquire,
      release,
    }),
    [active, acquire, release],
  );

  return (
    <OverlayAdCoordinatorContext.Provider value={value}>
      {children}
    </OverlayAdCoordinatorContext.Provider>
  );
};

export function useOverlayAdCoordinator(): OverlayAdCoordinatorApi {
  const ctx = useContext(OverlayAdCoordinatorContext);
  if (!ctx) {
    // Safe fallback when provider missing (tests / partial trees)
    return {
      active: getActiveOverlayClaim(),
      acquire: acquireOverlay,
      release: releaseOverlay,
    };
  }
  return ctx;
}

export default OverlayAdCoordinatorProvider;
